import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Package, Users, FileText, X, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { getProducts } from '@/services/productService';
import { getCustomers } from '@/services/customerService';
import { getInvoices } from '@/services/invoiceService';
import { formatCurrency } from '@/utils/invoiceCalculations';
import type { Product, Customer, Invoice } from '@/types';

interface SearchResult {
  id: string;
  type: 'product' | 'customer' | 'invoice';
  title: string;
  subtitle: string;
  badge?: string;
  path: string;
}

const categoryLabel: Record<SearchResult['type'], string> = {
  product: 'Products',
  customer: 'Customers',
  invoice: 'Estimates',
};

const categoryIcon: Record<SearchResult['type'], React.ReactNode> = {
  product: <Package size={14} />,
  customer: <Users size={14} />,
  invoice: <FileText size={14} />,
};

const categoryColor: Record<SearchResult['type'], string> = {
  product: 'bg-violet-100 text-violet-600',
  customer: 'bg-blue-100 text-blue-600',
  invoice: 'bg-emerald-100 text-emerald-600',
};

const GlobalSearch: React.FC = () => {
  const { companyId } = useAuthContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cache data to avoid refetching on every keystroke
  const cacheRef = useRef<{
    products: Product[];
    customers: Customer[];
    invoices: Invoice[];
    loaded: boolean;
  }>({ products: [], customers: [], invoices: [], loaded: false });

  const loadAll = useCallback(async () => {
    if (!companyId || cacheRef.current.loaded) return;
    try {
      const [products, customers, invoices] = await Promise.all([
        getProducts(companyId),
        getCustomers(companyId),
        getInvoices(companyId),
      ]);
      cacheRef.current = { products, customers, invoices, loaded: true };
    } catch {
      // silent fail — search just won't return results
    }
  }, [companyId]);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    await loadAll();
    const lower = q.toLowerCase();
    const { products, customers, invoices } = cacheRef.current;

    const productResults: SearchResult[] = products
      .filter(p =>
        p.name.toLowerCase().includes(lower) ||
        p.productCode.toLowerCase().includes(lower) ||
        p.hsnCode?.toLowerCase().includes(lower)
      )
      .slice(0, 3)
      .map(p => ({
        id: p.id,
        type: 'product',
        title: p.name,
        subtitle: `${p.productCode} · ${p.unit} · ${formatCurrency(p.price)}`,
        badge: `${p.gstPercentage}% GST`,
        path: `/products/${p.id}`,
      }));

    const customerResults: SearchResult[] = customers
      .filter(c =>
        c.name.toLowerCase().includes(lower) ||
        c.email.toLowerCase().includes(lower) ||
        c.mobile.includes(lower) ||
        c.customerCode.toLowerCase().includes(lower)
      )
      .slice(0, 3)
      .map(c => ({
        id: c.id,
        type: 'customer',
        title: c.name,
        subtitle: `${c.email} · ${c.mobile}`,
        badge: c.customerCode,
        path: `/customers/${c.id}`,
      }));

    const invoiceResults: SearchResult[] = invoices
      .filter(inv =>
        inv.invoiceNumber?.toLowerCase().includes(lower) ||
        inv.customerName?.toLowerCase().includes(lower) ||
        inv.status?.toLowerCase().includes(lower)
      )
      .slice(0, 3)
      .map(inv => ({
        id: inv.id,
        type: 'invoice',
        title: inv.invoiceNumber || `Estimate #${inv.id.slice(-6)}`,
        subtitle: `${inv.customerName} · ${formatCurrency(inv.grandTotal ?? 0)}`,
        badge: inv.status,
        path: `/invoices/${inv.id}`,
      }));

    setResults([...productResults, ...customerResults, ...invoiceResults]);
    setLoading(false);
    setActiveIdx(-1);
  }, [loadAll]);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => runSearch(query), 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut: Ctrl+K / Cmd+K to focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      goTo(results[activeIdx]);
    }
  };

  const goTo = (result: SearchResult) => {
    navigate(result.path);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const clear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Group results by type
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  const showDropdown = open;

  return (
    <div ref={containerRef} className="relative" style={{ width: 280 }}>
      {/* Input */}
      <div
        className={`flex items-center gap-2 border rounded-xl px-3 py-2 transition-all duration-200 ${
          open
            ? 'border-primary-400 bg-white shadow-md shadow-primary-100/40'
            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
        }`}
      >
        {loading ? (
          <Loader2 size={15} className="text-primary-500 animate-spin flex-shrink-0" />
        ) : (
          <Search size={15} className="text-gray-400 flex-shrink-0" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search by name, estimate no, mobile…"
          className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none flex-1 min-w-0"
          autoComplete="off"
          spellCheck={false}
        />
        {query ? (
          <button
            onClick={clear}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X size={14} />
          </button>
        ) : (
          <kbd className="hidden sm:inline-flex items-center text-[10px] text-gray-400 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono flex-shrink-0 select-none">
            Ctrl K
          </kbd>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute top-full mt-2 left-0 right-0 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 overflow-hidden animate-fade-in"
          style={{ minWidth: 340, right: 'auto' }}
        >
          {!query.trim() ? (
            // Empty state — show search hints
            <div className="py-4 px-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Search across</p>
              <div className="space-y-2">
                {[
                  { icon: <FileText size={13} />, color: 'bg-emerald-100 text-emerald-600', label: 'Invoices', hint: 'Invoice number, customer name, status (paid / unpaid)' },
                  { icon: <Users size={13} />, color: 'bg-blue-100 text-blue-600', label: 'Customers', hint: 'Full name, email, mobile, customer code' },
                  { icon: <Package size={13} />, color: 'bg-violet-100 text-violet-600', label: 'Products', hint: 'Product name, product code, HSN code' },
                ].map(({ icon, color, label, hint }) => (
                  <div key={label} className="flex items-start gap-3 p-2.5 rounded-xl bg-gray-50">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>{icon}</div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">{label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-3 text-center">
                Press <kbd className="font-mono bg-gray-100 border border-gray-200 rounded px-1">Esc</kbd> to close
              </p>
            </div>
          ) : loading && results.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
              <Loader2 size={16} className="animate-spin" />
              Searching…
            </div>
          ) : results.length === 0 && query.trim() ? (
            <div className="py-8 text-center">
              <Search size={24} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No results for <strong className="text-gray-600">"{query}"</strong></p>
            </div>
          ) : (
            <div className="py-2 max-h-96 overflow-y-auto">
              {(Object.entries(grouped) as [SearchResult['type'], SearchResult[]][]).map(([type, items]) => (
                <div key={type}>
                  {/* Category header */}
                  <div className="flex items-center gap-2 px-4 py-1.5 mt-1">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColor[type]}`}>
                      {categoryIcon[type]}
                      {categoryLabel[type]}
                    </span>
                  </div>

                  {/* Results */}
                  {items.map(result => {
                    const globalIdx = results.indexOf(result);
                    const isActive = activeIdx === globalIdx;
                    return (
                      <button
                        key={result.id}
                        onClick={() => goTo(result)}
                        onMouseEnter={() => setActiveIdx(globalIdx)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors group ${
                          isActive ? 'bg-primary-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        {/* Icon avatar */}
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${categoryColor[type]}`}>
                          {categoryIcon[type]}
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold truncate ${isActive ? 'text-primary-700' : 'text-gray-800'}`}>
                            {highlight(result.title, query)}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{result.subtitle}</p>
                        </div>

                        {/* Badge */}
                        {result.badge && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                            result.badge === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            result.badge === 'unpaid' ? 'bg-red-100 text-red-700' :
                            result.badge === 'partial' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {result.badge}
                          </span>
                        )}

                        <ArrowRight size={14} className={`flex-shrink-0 transition-transform ${isActive ? 'text-primary-500 translate-x-0.5' : 'text-gray-300 opacity-0 group-hover:opacity-100'}`} />
                      </button>
                    );
                  })}
                </div>
              ))}

              {/* Footer hint */}
              <div className="border-t border-gray-50 mt-1 px-4 py-2 flex items-center gap-4 text-[11px] text-gray-400">
                <span><kbd className="font-mono bg-gray-100 px-1 rounded">↑↓</kbd> navigate</span>
                <span><kbd className="font-mono bg-gray-100 px-1 rounded">Enter</kbd> open</span>
                <span><kbd className="font-mono bg-gray-100 px-1 rounded">Esc</kbd> close</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Highlight matching substring in title */
function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary-100 text-primary-800 rounded px-0.5 not-italic">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default GlobalSearch;
